import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Modal as MuiModal } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface CustomModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  customTitle?: React.ReactNode;
  appendCloseButtonToCustomTitle?: boolean;
  children: React.ReactNode;
  ariaDescribedBy?: string;
  modalClass?: string;
  /**
   * Classes for the wrapper around {@link children}. Opt-in and unset by default, so every
   * existing modal keeps its plain block layout. Bug 28140 needs it: the import modal's mapping
   * table can only size itself against the modal's own height if this wrapper joins the flex
   * chain between #modal-wrapper and the scroll area.
   */
  contentClass?: string;
  id: string;
}

export default function CustomModal({
  open,
  onClose,
  title,
  customTitle,
  appendCloseButtonToCustomTitle = true,
  ariaDescribedBy,
  modalClass,
  contentClass,
  id,
  children,
}: CustomModalProps) {
  const titleId = title ? `${id}-label` : undefined;
  const useCustomTitle = !!customTitle;
  const describedById = ariaDescribedBy ? `${id}-desc` : undefined;

  // Detect scrollbar width and adjust modal position
  const [scrollbarOffset, setScrollbarOffset] = useState(0);
  const scrollbarWidthRef = useRef<number | null>(null);

  useEffect(() => {
    // Cache the scrollbar width on mount (before any modal opens)
    if (scrollbarWidthRef.current === null) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      scrollbarWidthRef.current = scrollbarWidth;
    }

    // Apply the offset when modal opens
    if (open && scrollbarWidthRef.current !== null) {
      // Shift modal left by the scrollbar width
      setScrollbarOffset(scrollbarWidthRef.current);
    }
  }, [open]);

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={describedById}
      aria-modal="true"
      role="dialog"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        //width: "100% !important",
      }}
    >
      {/* 1424 px wide because the underlying page is 1440px wide with 8px padding on either side       */}
      <div
        id="modal-wrapper"
        className={`bg-white shadow-lg rounded-lg p-6 w-full xl1440:w-[1424px] p-[1.5rem] flex gap-[0.81rem] flex-col ${modalClass}`}
        style={{
          // Shift left to align with page content when scrollbar is present
          marginLeft: scrollbarOffset > 0 ? `-${scrollbarOffset}px` : undefined,
        }}
        role="document"
      >
        {useCustomTitle && (
          <div className="flex justify-between items-start">
            {customTitle}
            {appendCloseButtonToCustomTitle && (
              <IconButton onClick={onClose} aria-label="Close modal">
                <CloseIcon />
              </IconButton>
            )}
          </div>
        )}
        {!useCustomTitle && title && (
          <div className="flex justify-between items-center">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            <IconButton onClick={onClose} aria-label="Close modal">
              <CloseIcon />
            </IconButton>
          </div>
        )}
        <div id={describedById} className={contentClass}>
          {children}
        </div>
      </div>
    </MuiModal>
  );
}
